import { getLanguageStatic } from "@/lib/i18n/LanguageContext";
import en from "@/lib/i18n/translations/en";
import et from "@/lib/i18n/translations/et";
import type { Language } from "@/lib/i18n/types";
import type { DocumentIssueRoot } from "@/domains/pm/types";

const TRANSLATIONS = { en, et } as const;

/**
 * The title an issue shows, given what the database holds.
 *
 * The parent issue that groups a document's comment threads is stored with a
 * canonical British-English title ("Comments for plan.pdf") so every consumer
 * reading the row directly — the digest email, the chatbot, notifications —
 * sees something sensible. The app renders it in the VIEWER's language from
 * the root's filename instead, for as long as `auto_title` holds. The moment
 * a person renames the issue the database clears that flag and their title
 * wins everywhere, in every language.
 */
export function issueDisplayTitle(
  storedTitle: string,
  root: DocumentIssueRoot | null | undefined,
  language: Language = getLanguageStatic()
): string {
  if (!root?.auto_title) return storedTitle;
  const t = TRANSLATIONS[language] ?? TRANSLATIONS.en;
  return t.pm.issues.detail.documentThreadsTitle.replace("{filename}", root.filename);
}

/**
 * Whether saving `nextTitle` for `issue` would be a rename. A form seeded with
 * the display title and submitted untouched must not write that localised
 * string back — the database would read it as a rename and retire the
 * automatic title.
 */
export function isTitleUnchanged(
  issue: { title: string; title_raw?: string },
  nextTitle: string
): boolean {
  const next = nextTitle.trim();
  return next === issue.title || (issue.title_raw !== undefined && next === issue.title_raw);
}
